import { z } from 'zod';

// Each member is `.strict()` so that a foreign key forces the union to fall
// through to the matching member. Without it, `z.unknown()` makes `eq` optional
// and the non-strict object strips unrecognized keys, so every non-`eq`
// operator (`in`, `nin`, `gt`, ...) collapses to the first member as `{}`.
export const ResourceTriggerComparisonOperator = z.union([
  z.object({ eq: z.unknown() }).strict(),
  z.object({ ne: z.unknown() }).strict(),
  z.object({ gt: z.unknown() }).strict(),
  z.object({ gte: z.unknown() }).strict(),
  z.object({ lt: z.unknown() }).strict(),
  z.object({ lte: z.unknown() }).strict(),
  z.object({ in: z.array(z.unknown()) }).strict(),
  z.object({ nin: z.array(z.unknown()) }).strict(),
  z.object({ exists: z.boolean() }).strict(),
  z.object({ contains: z.string() }).strict(),
]);
export type ResourceTriggerComparisonOperator = z.infer<typeof ResourceTriggerComparisonOperator>;

export const ResourceTriggerDiffCriterion = z.record(
  z.union([
    ResourceTriggerComparisonOperator,
    z.object({
      from: z.unknown(),
      to: z.unknown(),
    }),
  ])
);
export type ResourceTriggerDiffCriterion = z.infer<typeof ResourceTriggerDiffCriterion>;

export const ResourceTriggerRelationshipCriterion = z.record(
  z.object({
    where: z.record(ResourceTriggerComparisonOperator),
  })
);
export type ResourceTriggerRelationshipCriterion = z.infer<
  typeof ResourceTriggerRelationshipCriterion
>;

const ResourceTriggerLeafCriteria = z
  .object({
    diff: ResourceTriggerDiffCriterion.optional(),
    relationships: ResourceTriggerRelationshipCriterion.optional(),
  })
  .strict()
  .refine((data) => data.diff ?? data.relationships ?? Object.keys(data).length === 0, {
    message:
      "Criteria must be an empty object {} (match all) or contain at least one of 'diff' or 'relationships'",
  });
type ResourceTriggerLeafCriteriaType = z.infer<typeof ResourceTriggerLeafCriteria>;

export type ResourceTriggerCriteria =
  | { and: ResourceTriggerCriteria[] }
  | { or: ResourceTriggerCriteria[] }
  | { not: ResourceTriggerCriteria }
  | ResourceTriggerLeafCriteriaType;

export const ResourceTriggerCriteria: z.ZodType<ResourceTriggerCriteria> = z.lazy(() =>
  z.union([
    z.object({ and: z.array(ResourceTriggerCriteria) }).strict(),
    z.object({ or: z.array(ResourceTriggerCriteria) }).strict(),
    z.object({ not: ResourceTriggerCriteria }).strict(),
    ResourceTriggerLeafCriteria,
  ])
);
